import { getShopifyAppProxyRoutePath, isShopifyAppProxySurface, type HostingServingSurface } from '../utils/domain';

/**
 * The concierge on the ORIGIN WE ARE ALREADY ON, when that origin serves it.
 *
 * `canonicalConciergeUrl` is built server-side from the Shopify *admin handle*
 * (`<shop>.myshopify.com`), because that is the only domain recorded against the
 * app. Shopify then 301s it to the merchant's primary domain, so expanding the
 * bubble bounced through a redirect and briefly showed a `myshopify.com` URL the
 * shopper has never seen — see lessons-learned §6, "a store's admin handle is
 * not its permanent domain".
 *
 * Keyed on the SURFACE, not on the injected `__BC_ROUTE_PATH__`: the bubble also
 * runs on ordinary storefront pages (a PDP), where no App Proxy skeleton ran and
 * that global is absent — which is exactly where the redirect was still visible.
 * `getShopifyAppProxyRoutePath` supplies the default sub-path in that case, and
 * both places the bubble can run are the merchant's own storefront, so the
 * current origin is their real domain either way.
 *
 * Returns null off that surface, where the server-computed canonical URL is
 * genuinely the best answer (a real cross-surface link) and guessing an origin
 * would send the shopper somewhere that serves nothing.
 *
 * Lives in its own module so it is unit-testable without dragging in the whole
 * widget's module graph.
 */
export function sameOriginConciergeBase(servingSurface?: HostingServingSurface | null): string | null {
  if (typeof window === 'undefined') return null;
  if (!isShopifyAppProxySurface(servingSurface)) return null;
  const routePath = getShopifyAppProxyRoutePath(servingSurface);
  if (!routePath) return null;
  return `${window.location.origin}${routePath}/concierge`;
}
